import React, { useState, useEffect } from 'react';
import {
  MapPin,
  Activity,
  ArrowDown
} from 'lucide-react';
import { AnalyticsEvent } from '../../types';
import { dataStore } from '../../services/dataStore';

export const AnalyticsDashboard: React.FC = () => {
  const [events, setEvents] = useState<AnalyticsEvent[]>(() => dataStore.getAnalyticsEvents());
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    void dataStore.refreshAnalyticsFromDatabase().then(() => {
      setEvents(dataStore.getAnalyticsEvents());
      setLoadError('');
    }).catch((error: unknown) => {
      setLoadError(error instanceof Error ? error.message : 'تعذر تحميل التحليلات.');
    });
    return dataStore.subscribe(() => {
      setEvents(dataStore.getAnalyticsEvents());
    });
  }, []);

  const totalEvents = events.length;
  const uniqueVisitors = new Set(events.map((e) => e.visitor_id)).size;

  // Event counts
  const pageViews = events.filter((e) => e.event_name === 'PAGE_VIEW').length;
  const productViews = events.filter((e) => e.event_name === 'PRODUCT_VIEW').length;
  const cartAdds = events.filter((e) => e.event_name === 'ADD_TO_CART').length;
  const checkouts = events.filter((e) => e.event_name === 'BEGIN_CHECKOUT').length;
  const orders = events.filter((e) => e.event_name === 'ORDER_CREATED').length;

  // Approximate Governorates Breakdown
  const govCounts: Record<string, number> = {};

  events.forEach((e) => {
    if (e.location_governorate) {
      govCounts[e.location_governorate] = (govCounts[e.location_governorate] || 0) + 1;
    }
  });

  const totalGovHits = Math.max(1, Object.values(govCounts).reduce((a, b) => a + b, 0));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
      {loadError && (
        <div role="alert" style={{ padding: '12px 16px', borderRadius: '8px', color: '#991B1B', backgroundColor: '#FEF2F2', border: '1px solid #FECACA' }}>
          {loadError}
        </div>
      )}
      {/* Overview Stat Row */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px'
        }}
      >
        <div style={{ backgroundColor: '#FFFFFF', padding: '18px', borderRadius: 'var(--radius-lg)', border: '1px solid #E2E8F0' }}>
          <div style={{ fontSize: '0.78rem', color: '#64748B', fontWeight: 600 }}>إجمالي الأحداث المسجلة</div>
          <div style={{ fontSize: '1.6rem', fontWeight: 900, color: '#1E293B', marginTop: '4px', fontFamily: 'Cairo, sans-serif' }}>
            {totalEvents}
          </div>
          <div style={{ fontSize: '0.72rem', color: '#64748B', marginTop: '4px' }}>آخر {totalEvents} حدث محمّل</div>
        </div>

        <div style={{ backgroundColor: '#FFFFFF', padding: '18px', borderRadius: 'var(--radius-lg)', border: '1px solid #E2E8F0' }}>
          <div style={{ fontSize: '0.78rem', color: '#64748B', fontWeight: 600 }}>الزوار الفريدين (Unique)</div>
          <div style={{ fontSize: '1.6rem', fontWeight: 900, color: '#1E293B', marginTop: '4px', fontFamily: 'Cairo, sans-serif' }}>
            {uniqueVisitors}
          </div>
          <div style={{ fontSize: '0.72rem', color: '#64748B', marginTop: '4px' }}>جلسات مجهولة الهوية وآمنة</div>
        </div>

        <div style={{ backgroundColor: '#FFFFFF', padding: '18px', borderRadius: 'var(--radius-lg)', border: '1px solid #E2E8F0' }}>
          <div style={{ fontSize: '0.78rem', color: '#64748B', fontWeight: 600 }}>مشاهدات المنتجات</div>
          <div style={{ fontSize: '1.6rem', fontWeight: 900, color: '#1E293B', marginTop: '4px', fontFamily: 'Cairo, sans-serif' }}>
            {productViews}
          </div>
          <div style={{ fontSize: '0.72rem', color: '#3B82F6', marginTop: '4px' }}>تفاعل وتصفح عالي</div>
        </div>

        <div style={{ backgroundColor: '#FFFFFF', padding: '18px', borderRadius: 'var(--radius-lg)', border: '1px solid #E2E8F0' }}>
          <div style={{ fontSize: '0.78rem', color: '#64748B', fontWeight: 600 }}>الطلبات المسجلة</div>
          <div style={{ fontSize: '1.6rem', fontWeight: 900, color: '#047857', marginTop: '4px', fontFamily: 'Cairo, sans-serif' }}>
            {orders}
          </div>
          <div style={{ fontSize: '0.72rem', color: '#059669', fontWeight: 700, marginTop: '4px' }}>
            معدل تحويل: {((orders / Math.max(1, uniqueVisitors)) * 100).toFixed(1)}%
          </div>
        </div>
      </div>

      {/* Funnel & Governorate Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '24px' }}>
        {/* Geographic Distribution (Egyptian Governorates) */}
        <div style={{ backgroundColor: '#FFFFFF', padding: '24px', borderRadius: 'var(--radius-lg)', border: '1px solid #E2E8F0', boxShadow: 'var(--shadow-xs)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <MapPin size={20} color="var(--primary-700)" />
            <h3 style={{ fontSize: '1.1rem', fontWeight: 800, color: '#1E293B', fontFamily: 'Cairo, sans-serif' }}>
              التوزيع الجغرافي التقريبي (محافظات مصر)
            </h3>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {Object.entries(govCounts).length === 0 ? (
              <p style={{ margin: 0, color: '#64748B', fontSize: '0.85rem' }}>لا توجد بيانات جغرافية مسجلة بعد.</p>
            ) : Object.entries(govCounts).map(([gov, count]) => {
              const pct = Math.round((count / totalGovHits) * 100);
              return (
                <div key={gov}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: '4px' }}>
                    <span style={{ fontWeight: 600, color: '#334155' }}>{gov}</span>
                    <span style={{ fontWeight: 800, color: 'var(--primary-800)' }}>{pct}% ({count})</span>
                  </div>
                  <div style={{ width: '100%', height: '7px', backgroundColor: '#F1F5F9', borderRadius: '4px', overflow: 'hidden' }}>
                    <div style={{ width: `${pct}%`, height: '100%', backgroundColor: 'var(--primary-600)' }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Detailed Funnel */}
        <div style={{ backgroundColor: '#FFFFFF', padding: '24px', borderRadius: 'var(--radius-lg)', border: '1px solid #E2E8F0', boxShadow: 'var(--shadow-xs)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <Activity size={20} color="var(--accent-600)" />
            <h3 style={{ fontSize: '1.1rem', fontWeight: 800, color: '#1E293B', fontFamily: 'Cairo, sans-serif' }}>
              مراحل قمع التحويل (Step-by-Step Funnel)
            </h3>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ padding: '10px 14px', backgroundColor: '#F8FAFC', borderRadius: '8px', border: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>1. تصفح الصفحات الرئيسية والكتالوج</div>
                <div style={{ fontSize: '0.75rem', color: '#64748B' }}>Page Views</div>
              </div>
              <span style={{ fontWeight: 800, fontSize: '1.1rem' }}>{pageViews}</span>
            </div>

            <div style={{ textAlign: 'center', color: '#94A3B8' }}><ArrowDown size={16} /></div>

            <div style={{ padding: '10px 14px', backgroundColor: '#F8FAFC', borderRadius: '8px', border: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>2. فتح تفاصيل المنتجات</div>
                <div style={{ fontSize: '0.75rem', color: '#64748B' }}>Product Views</div>
              </div>
              <span style={{ fontWeight: 800, fontSize: '1.1rem' }}>{productViews}</span>
            </div>

            <div style={{ textAlign: 'center', color: '#94A3B8' }}><ArrowDown size={16} /></div>

            <div style={{ padding: '10px 14px', backgroundColor: '#F8FAFC', borderRadius: '8px', border: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>3. إضافة المنتجات لسلة الطلب</div>
                <div style={{ fontSize: '0.75rem', color: '#64748B' }}>Add to Cart</div>
              </div>
              <span style={{ fontWeight: 800, fontSize: '1.1rem', color: '#3B82F6' }}>{cartAdds}</span>
            </div>

            <div style={{ textAlign: 'center', color: '#94A3B8' }}><ArrowDown size={16} /></div>

            <div style={{ padding: '10px 14px', backgroundColor: '#ECFDF5', borderRadius: '8px', border: '1px solid #A7F3D0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>4. بدء إتمام الطلب</div>
                <div style={{ fontSize: '0.75rem', color: '#64748B' }}>Begin Checkout</div>
              </div>
              <span style={{ fontWeight: 800, fontSize: '1.1rem' }}>{checkouts}</span>
            </div>

            <div style={{ textAlign: 'center', color: '#94A3B8' }}><ArrowDown size={16} /></div>

            <div style={{ padding: '10px 14px', backgroundColor: '#ECFDF5', borderRadius: '8px', border: '1px solid #A7F3D0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: '0.9rem', color: '#047857' }}>5. تسجيل الطلب</div>
                <div style={{ fontSize: '0.75rem', color: '#065F46' }}>Persisted Orders</div>
              </div>
              <span style={{ fontWeight: 900, fontSize: '1.25rem', color: '#047857' }}>{orders}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Real-time Event Stream */}
      <div style={{ backgroundColor: '#FFFFFF', padding: '24px', borderRadius: 'var(--radius-lg)', border: '1px solid #E2E8F0', boxShadow: 'var(--shadow-xs)' }}>
        <h3 style={{ fontSize: '1.05rem', fontWeight: 800, color: '#1E293B', marginBottom: '14px', fontFamily: 'Cairo, sans-serif' }}>
          أحدث أحداث الزوار المسجلة (Event Stream)
        </h3>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem', textAlign: 'right' }}>
            <thead>
              <tr style={{ backgroundColor: '#F8FAFC', borderBottom: '1px solid #E2E8F0', color: '#64748B' }}>
                <th style={{ padding: '8px 12px' }}>الحدث</th>
                <th style={{ padding: '8px 12px' }}>معرّف الجلسة</th>
                <th style={{ padding: '8px 12px' }}>نوع الجهاز</th>
                <th style={{ padding: '8px 12px' }}>المصدر</th>
                <th style={{ padding: '8px 12px' }}>المحافظة</th>
                <th style={{ padding: '8px 12px' }}>الوقت</th>
              </tr>
            </thead>
            <tbody>
              {events.slice(0, 10).map((evt) => (
                <tr key={evt.id} style={{ borderBottom: '1px solid #F1F5F9' }}>
                  <td style={{ padding: '8px 12px', fontWeight: 700, color: 'var(--primary-800)' }}>
                    {evt.event_name}
                  </td>
                  <td style={{ padding: '8px 12px', fontFamily: 'monospace', color: '#64748B' }}>
                    {evt.session_id.substring(0, 12)}...
                  </td>
                  <td style={{ padding: '8px 12px' }}>{evt.device_type}</td>
                  <td style={{ padding: '8px 12px' }}>{evt.traffic_source}</td>
                  <td style={{ padding: '8px 12px' }}>{evt.location_governorate || '—'}</td>
                  <td style={{ padding: '8px 12px', color: '#94A3B8' }}>
                    {new Date(evt.timestamp).toLocaleTimeString('ar-EG')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
